import {defineStore} from "pinia";
import {UserService} from "../services/user.service";
import {OrgMemberService} from "@/javascript/services/orgMember.service";
import {OrganizationService} from "../services/organization.service";
import {ApplicationHelper} from "@/javascript/helpers/ApplicationHelper";

export const editorRole = "Editor";
export const managerRole = "Manager";
export const ownerRole = "Owner";

export const teamRoles = [
  editorRole,
  managerRole,
  ownerRole
];


export const useOrgMemberStore = defineStore('orgMember', {
  state: () => ({
    // orgMemberService: new OrgMemberService(),
    orgMemberService: null,
    organizationService: null,
    userService: new UserService(),
    organizationId: null,
    members: [],
    currentRecord: {},
    loading: true,
    loadingRecord: true,
    permissions: {},
    meta: {
      page: 1,
      pages: 1
    },
    filter: {
      sort: {
        key: "created_at",
        order: "asc"
      },
      search: {
        key: "user_first_name_or_user_last_name_or_user_email_cont", // Ransack search query, add more here to expand the search results
        term: null
      },
      filters: { // Dropdown select filters, need to instantiate initial value
        role: ''
      }
    },
    editSettings: false,
    localMember: null,
    errors: {},
    showRemoveModal: false,
    removeConfirmationMessage: '',
    // these are for analytic events
    traceableType: "OrgMember",
    traceableName: "org-member",

    //fields for uploader
    modelName: "Team Member",
    tempRecords: [],
    modelTemplate: {
      "f_name": {
        type: "string",
        value: ""
      },
      "l_name": {
        type: "string",
        value: ""
      },

      "email": {
        type: "string",
        value: ""
      },
      "role": {
        type: "select",
        options: [
          "Editor", "Manager",
        ],
        value: ""
      },
    }
  }),

  actions: {
    setOrganizationId(id) {
      if (id) {
        this.organizationId = id;
        this.orgMemberService = new OrgMemberService(id);
        this.organizationService = new OrganizationService(id);
      } else {
        throw `Organization ID is not set: ${id}`;
      }
    },
    async loadData() {
      try {
        await this.getMembers();
      } catch (e) {
        console.error("Error loading data", e);
      }
    },
    async getMember(id) {
      this.loadingRecord = true;
      const res = await this.orgMemberService.show(id);
      this.currentRecord = res.record;
      this.permissions = res.permissions;
      this.loadingRecord = false;
    },
    async getMembers() {
      this.loading = true;
      try {
        let params = new URLSearchParams();
        const res = await this.orgMemberService.paginate(this.meta.page, params, this.filter);
        this.members = res.data;
        this.permissions = res.permissions;
        this.meta = res.meta;
      } catch (e) {
        console.error("Error fetching members", e);
      } finally {
        this.loading = false;
      }
    },
    /**
     * Method used in generic Dashboard Show component to load the record.
     * @param id
     */
    async getRecord(id) {
      await this.getMember(id);
    },
    nextPage() {
      this.meta.page++;
      this.getMembers();
    },
    prevPage() {
      this.meta.page--;
      this.getMembers();
    },
    moveToPage(page) {
      this.meta.page = page;
      this.getMembers();
    },
    toggleEditSettings() {
      this.editSettings = !this.editSettings;
      if (this.editSettings) {
        this.localMember = {...this.currentRecord};  // Create a copy of member data
      } else {
        this.localMember = null;  // Discard the copy if editing is canceled
        this.errors = {};
      }
    },
    async saveSettings() {
      if (this.localMember) {
        try {
          await this.orgMemberService.update(this.currentRecord.id, this.localMember);
          // Update the member in the members array
          const index = this.members.findIndex(m => m.id === this.currentRecord.id);
          if (index !== -1) {
            this.members[index] = {...this.localMember};
          }
          this.currentRecord = {...this.localMember};  // Update original member data
          this.toggleEditSettings();
          this.errors = {};
        } catch (error) {
          console.error("Failed to save member settings", error);
        }
      }
    },
    cancelEditSettings() {
      this.toggleEditSettings();  // Just toggle off edit mode and discard changes
    },
    closeRemoveModal() {
      this.showRemoveModal = false;
    },
    // Methods to conform to excel uploader
    addToTempList(data) {
      this.tempRecords.push(data);
    },
    downloadTemplate() {
      this.organizationService.downloadTemplate(this.organizationId);
    },
    async parseTemplate(file) {
      const data = await ApplicationHelper.excelFileToJson(file);
      let recordList = [];
      if (data) {
        data.forEach(item => {
          // Need to convert the headers from backend to proper format
          let newRecord = {};
          newRecord['f_name'] = item["First Name"];
          newRecord['l_name'] = item["Last Name"];
          newRecord['email'] = item["Email"];
          newRecord['role'] = item["Role"];
          if (this.recordValid(newRecord)) {
            recordList.push(newRecord);
          }
        });
      }
      return recordList;
    },
    recordValid(record) {
      return record.f_name
        && record.l_name
        && record.email
        && ['Owner', 'Manager', 'Editor'].includes(record.role);
    },
    async addRecords() {
      for (const record of this.tempRecords) {
        const params = {
          ...record,
          "organization_id": this.organizationId
        };
        await this.organizationService.uploadMember(this.organizationId, params);
        record.uploaded = true;
      }
      await this.getMembers();
    },
    async updateRecord(data) {
      await this.updateTeamMember(data);
    },
    async updateTeamMember(data) {
      this.loadingRecord = true;
      let params = {
        ...data
      };
      let res = await this.orgMemberService.update(this.currentRecord.id, params);
      await this.getMember(this.currentRecord.id);
      await this.getMembers();
      return res;
    },
    async removeMemberFromTeam() {
      try {
        return await this.orgMemberService.update(this.currentRecord.id, {
          archived: true
        });
      } catch (error) {
        console.error("Failed to remove member from team", error);
      }
    },


    // Sets the Ransack sort terms based on the passed key
    // This gets passed to index method to sort the paginated results
    sort(key) {
      if (this.filter.sort.key === key) {
        // If the passed key is the current key, flip the current sort order
        this.filter.sort.order = this.filter.sort.order === 'asc' ? 'desc' : 'asc';
      } else {
        // Otherwise set the new key and order by Ascending
        this.filter.sort.key = key;
        this.filter.sort.order = 'asc';
      }

      this.getMembers();
    },

    search(term) {
      this.filter.search.term = term;
      this.meta.page = 1; //this makes it paginate from page 1 not just current page (resets pagination)
      this.getMembers();
    },

    filterRecords(key, value) {
      this.filter.filters[key] = value;
      this.getMembers();
    },
    async validate(local) {
      this.errors = {};
      if (!local.role) {
        this.errors['name'] = "Name required";
      }
    },
  },
  getters: {
    teamRoles() {
      return teamRoles;
    },
    /**
     * Method used in generic Dashboard Index component.
     * Returns an object containing the index table data.
     * @param state
     */
    getIndexTableData(state) {
      let labels = [
        {label: 'First Name', key: 'f_name', ransackKey: "user_first_name", sort: true},
        {label: 'Last Name', key: 'l_name', ransackKey: "user_last_name", sort: true},
        {label: 'Email', key: 'email', ransackKey: 'user_email', sort: true},
        {label: 'Giving Visibility', key: 'giving_visibility', sort: false},
        {label: 'Role', key: 'role', sort: false, select: true, options: ['Owner', 'Editor', 'Manager']},
      ];
      // Splice in the org field if the current org has any children.
      // So it only shows for multi level orgs
      if(state.organization?.child_ids?.length) {
        labels.splice(3, 0, {label: "Organization", key: 'organization_name', sort: true, ransackKey: "organization_name"});
      }
      if (state.members.length > 0) {
        let members = state.members.map((member) => {
          return {
            id: member.id,
            f_name: member.user_details.f_name,
            l_name: member.user_details.l_name,
            email: member.user_details.email,
            role: member.role,
            organization_name: member.organization_name,
            giving_visibility: member.giving_blind ? 'Hidden' : 'Visible'
          };
        });
        return {
          labels: labels,
          rows: members
        };
      } else {
        return {
          labels: labels,
          rows: []
        };
      }
    },
    /**
     * Method used in generic Dashboard Show component.
     * Returns an object containing the record table data.
     * @param state
     */
    getShowTableData(state) {
      let labels = [
        'label',
        'data',
      ];
      if (state.currentRecord && Object.hasOwn(state.currentRecord, 'id')) {
        let rows = [
          {
            label: 'Full Name',
            data: `${state.currentRecord.user_details.f_name} ${state.currentRecord.user_details.l_name}`
          },
          {
            label: 'Email',
            data: state.currentRecord.user_details.email
          },
          {
            label: 'Role',
            data: state.currentRecord.role
          },
          {
            label: 'Giving Visibility',
            data: state.currentRecord.giving_blind ? 'Hidden (giving blind)' : 'Full access'
          },
        ];
        return {
          labels: labels,
          rows: rows
        };
      } else {
        return {
          labels: labels,
          rows: []
        };
      }
    },
    /**
     * Method used in generic Dashboard Show component.
     * Returns an object with each key being the dashboard panel name of an association,
     *  and each value being the table data for that association.
     */
    getAssociationTableData() {
      return {};
    },
    /**
     * Method used in generic Dashboard Show component.
     * Returns the data needed for the editing form table.
     * @param state
     */
    getEditingTableData(state) {
      if (state.currentRecord && Object.hasOwn(state.currentRecord, 'id') && state.currentRecord.user_details) {
        return [
          {
            label: 'Full Name',
            input: 'readonly',
            data: `${state.currentRecord.user_details.f_name} ${state.currentRecord.user_details.l_name}`
          },
          {
            label: 'Email',
            input: 'readonly',
            data: state.currentRecord.user_details.email
          },
          {
            label: 'Role',
            key: 'role',
            input: 'select',
            options: teamRoles.map(role => ({ label: role, value: role })),
          },
          {
            label: 'Hide individual giving amounts',
            key: 'giving_blind',
            input: 'checkbox',
          },
        ];
      } else {
        return [];
      }
    }
  }
});
